import React from "react";
import { Link } from "react-router-dom";
import Icon from "../../UI/Icon";
import content from "../../../content/cityLandings.json";
import { SITE_URL, canonicalUrl } from "../../../Service/seoUrl";
import * as endpoint from "../../../Service/endpoints";

// AEO city landing pages (/delhi/appliances, /noida/furniture, ...). The copy,
// the visible FAQ and the FAQPage schema all come from cityLandings.json.

export function getCityLanding(city, category) {
  const slug = endpoint.publicCategorySlug(category);
  return content.pages.find((page) => page.city === city && page.slug === slug) || null;
}

// Batch-2 combined pages: /delhi/furniture-and-appliances and friends.
export function getCityCombo(city) {
  return content.combos.find((page) => page.city === city) || null;
}

// Link text ("Appliances in Delhi") vs heading text ("Appliance rental in Delhi").
const shortLabel = (page) => {
  if (page.slug === "appliances") return "Appliances";
  if (page.slug === "furniture") return "Furniture";
  return "Furniture + appliances";
};

const headingLabel = (page) => {
  if (page.slug === "appliances") return "Appliance";
  if (page.slug === "furniture") return "Furniture";
  return "Furniture and appliance";
};

const bulletText = (bullet) => (typeof bullet === "string" ? bullet : bullet.text);

export function CityLandingLead({ page }) {
  return (
    <section className="v-landing-lead" aria-labelledby="landing-why">
      <h2 id="landing-why" className="v-landing-lead__title">{page.bulletsTitle}</h2>
      <ul className="v-landing-lead__list">
        {page.bullets.map((bullet) => {
          const text = bulletText(bullet);
          let body = text;
          if (bullet.to) body = <Link to={endpoint.getRoute(bullet.to, page.city)}>{text}</Link>;
          else if (bullet.href) body = <Link to={bullet.href}>{text}</Link>;
          return (
            <li key={text}>
              <Icon name="check" size={16} className="v-landing-lead__icon" />
              <span>{body}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Always-expanded Q&A (not an accordion) so answer engines and readers see the text.
export function CityLandingFaq({ page }) {
  const cityName = content.cities[page.city];
  // Cross-links: the other city pages, plus the combined furniture+appliance pages.
  const others = content.pages.concat(content.combos).filter((p) => p !== page);
  return (
    <>
      <section className="v-landing-faq" aria-labelledby="landing-faq">
        <h2 id="landing-faq" className="v-landing-faq__title">
          {headingLabel(page)} rental in {cityName}: FAQs
        </h2>
        <div className="v-landing-faq__list">
          {page.faqs.map((faq) => (
            <div className="v-landing-faq__item" key={faq.q}>
              <h3>{faq.q}</h3>
              <p>{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
      <nav className="v-landing-cities" aria-label="Rental in other cities">
        <h2 className="v-landing-cities__title">Also available in Delhi NCR</h2>
        <ul>
          {others.map((p) => (
            <li key={`${p.city}-${p.slug}`}>
              <Link to={`/${p.city}/${p.slug}`} className="v-chip">
                {shortLabel(p)} in {content.cities[p.city]}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

export function cityLandingSchema(page) {
  const cityName = content.cities[page.city];
  const pageUrl = canonicalUrl(`/${page.city}/${page.slug}`);
  const categoryTitle = headingLabel(page);
  const orgId = `${SITE_URL}/#organization`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["LocalBusiness", "Store"],
        "@id": orgId,
        name: "Voorent",
        url: `${SITE_URL}/`,
        email: "support@voorent.com",
        description:
          "Voorent rents furniture and appliances on flexible monthly subscriptions across Delhi NCR, with rent-to-own available on Premium furniture by mutual agreement.",
        areaServed: Object.values(content.cities).map((name) => ({ "@type": "City", name })),
        priceRange: "₹₹",
        knowsAbout: ["furniture rental", "appliance rental", "monthly rental subscription", "rent to own furniture"],
      },
      {
        "@type": "WebPage",
        "@id": pageUrl,
        url: pageUrl,
        name: page.title,
        description: page.description,
        inLanguage: "en-IN",
        about: { "@id": orgId },
        mainEntity: {
          "@type": "OfferCatalog",
          name: `${categoryTitle} rental in ${cityName}`,
          itemListElement: page.offers.map((item) => ({
            "@type": "Offer",
            // A rental is a service; using Service (not Product) keeps these out of
            // product rich-result validation, which would demand prices and reviews.
            itemOffered: { "@type": "Service", name: `${item} on rent in ${cityName}`, category: page.slug },
            areaServed: { "@type": "City", name: cityName },
            priceCurrency: "INR",
            businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
            seller: { "@id": orgId },
          })),
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: page.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
  };
}
